/**
 * Reader feedback on the Desk: every "Tell us straight" message for the title, or one piece's.
 * A modal in a portal like the Publish dialog: focus moves in, Tab is trapped, Esc closes, and focus
 * returns to the button that opened it. Read-only: triage stays in the Feedback inbox.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { describeError } from '../lib/describeError'
import { getDeskFeedback, type FeedbackKind, type FeedbackView } from './api'
import { filterByKind, KIND_DOT, KIND_LABEL, KINDS, kindChipClass, senderLabel } from './feedback'
import { etDate, etTime } from './labels'
import { pieceHref } from './links'
import { btn, focus, link } from './piece/ui'
import { FeedbackThread } from './FeedbackThread'

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
const ACCENT: Record<string, string> = { good: 'border-l-radar-green', bad: 'border-l-amber-glow', ugly: 'border-l-radar-red' }

type Props = {
  publicationSlug: string
  sectionLabel: string
  sectionPath: string
  /** Open on one piece's feedback; the reader can widen it to the whole title. */
  pieceId: number | null
  /** Admins get the way into the Feedback inbox. */
  showInbox: boolean
  /** Editors and admins see each conversation and may reply; writers read the messages only. */
  editor: boolean
  onClose: () => void
}

export function FeedbackDialog({ publicationSlug, sectionLabel, sectionPath, pieceId: initialPiece, showInbox, editor, onClose: close }: Props) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const [pieceId, setPieceId] = useState<number | null>(initialPiece)
  const [kind, setKind] = useState<FeedbackKind | null>(null)
  const [view, setView] = useState<FeedbackView>('active')
  const q = useQuery({
    queryKey: ['editorial-feedback', publicationSlug, pieceId, view],
    queryFn: () => getDeskFeedback(publicationSlug, pieceId, view),
    placeholderData: (prev) => prev,
  })
  const d = q.data
  // unsent replies: closing asks first, so nothing typed is lost
  const dirty = useRef(new Set<number>())
  const onDirty = (id: number, on: boolean) => { if (on) dirty.current.add(id); else dirty.current.delete(id) }
  const onClose = () => { if (dirty.current.size === 0 || window.confirm('Discard the reply you have not sent?')) close() }
  const shown = d ? filterByKind(d.items, kind) : []

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    box.current?.focus()
    return () => { if (opener && opener.isConnected) opener.focus() }
  }, [])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
    if (e.key !== 'Tab' || !box.current) return
    const els = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (els.length === 0) { e.preventDefault(); return }
    const first = els[0], last = els[els.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === box.current)) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
  }

  const tally = (k: FeedbackKind | null, label: string, n: number, dot?: string) => {
    const on = kind === k
    return (
      <button key={label} type="button" aria-pressed={on} onClick={() => setKind(k)}
              className={`flex min-h-10 items-center gap-2 rounded-full border px-3.5 text-[13px] font-semibold ${focus} ${
                on ? 'border-amber-glow bg-ink-600 text-paper ring-1 ring-amber-glow' : 'border-ink-500 text-paper-dim hover:bg-ink-600 hover:text-paper'}`}>
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />}
        {label}
        <span className="font-serif text-[17px] leading-none text-paper">{n}</span>
      </button>
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/60 px-4 py-12 font-ui" role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}
           className="flex max-h-[calc(100vh-6rem)] w-full max-w-[720px] flex-col rounded-2xl border border-ink-500 bg-ink-700 text-[14px] leading-snug text-paper shadow-xl focus:outline-none">
        <div className="flex items-start justify-between gap-4 border-b border-ink-500 px-7 pb-4 pt-6">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-paper-faint">{sectionLabel} · Reader feedback</span>
            <h2 id={titleId} className="m-0 font-serif text-[26px] font-medium leading-tight">
              {pieceId !== null ? (d?.piece?.title ?? 'This piece') : 'What readers told us'}
            </h2>
            <p className="m-0 text-[13px] text-paper-dim">
              {pieceId !== null ? 'Sent from this piece’s pages, newest first.' : 'Everything sent from the “Tell us straight” sheet, newest first.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className={`-mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[22px] leading-none text-paper-dim hover:bg-ink-600 hover:text-paper ${focus}`}>
            ×
          </button>
        </div>

        {d && (
          <div className="flex flex-wrap items-center gap-3 border-b border-ink-500 px-7 pt-3.5">
            <div role="tablist" aria-label="Which list" className="flex gap-5">
              {(['active', 'archived'] as const).map((v) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => { setView(v); setKind(null) }}
                        className={`-mb-px flex items-baseline gap-2 border-b-2 pb-2.5 text-[14px] font-semibold ${focus} ${
                          view === v ? 'border-amber-glow text-paper' : 'border-transparent text-paper-dim hover:text-paper'}`}>
                  {v === 'active' ? 'Active' : 'Archived'}
                  <span className={`font-serif text-[16px] leading-none ${view === v ? 'text-amber-glow' : 'text-paper-faint'}`}>{d.views[v]}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {d && (
          <div className="flex flex-wrap items-center gap-2 border-b border-ink-500 px-7 py-3.5" role="group" aria-label="Show">
            {tally(null, 'All', d.total)}
            {KINDS.map((k) => tally(k, KIND_LABEL[k], d.counts[k], KIND_DOT[k]))}
            {pieceId !== null && (
              <button type="button" onClick={() => setPieceId(null)} className={`ml-auto ${link} text-[13px]`}>All feedback for {sectionLabel}</button>
            )}
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-7 py-5">
          {q.isPending && <p role="status" className="m-0 text-paper-dim">Loading feedback…</p>}
          {q.isError && <p role="alert" className="m-0 text-radar-red">Could not load feedback. {describeError(q.error)}</p>}
          {d && shown.length === 0 && (
            <p className="m-0 rounded-xl border border-dashed border-ink-500 px-5 py-6 text-center text-paper-dim">
              {d.total === 0
                ? view === 'archived' ? 'Nothing archived yet.' : d.views.archived > 0 ? 'All caught up: everything is archived.' : pieceId !== null ? 'No feedback from this piece yet.' : 'No feedback yet.'
                : `No ${KIND_LABEL[kind ?? 'good'].toLowerCase()} feedback here.`}
            </p>
          )}
          {shown.map((m) => (
            <article key={m.id} className={`flex flex-col gap-2.5 rounded-xl border border-l-[3px] border-ink-500 bg-ink-800/40 px-4 py-3.5 ${ACCENT[m.kind] ?? ''}`}>
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[12px] text-paper-faint">
                  <span className={kindChipClass(m.kind)}>{KIND_LABEL[m.kind] ?? m.kind}</span>
                  {m.created_at && <span>{etDate(m.created_at)} · {etTime(m.created_at)}</span>}
                </div>
                <span className="min-w-0 break-all text-[12px] text-paper-faint">
                  {m.email ? <>{senderLabel({ ...m, email: null })} · <a href={`mailto:${m.email}`} className={`${link} text-[12px]`}>{m.email}</a></> : senderLabel(m)}
                </span>
              </div>
              <p className="m-0 whitespace-pre-wrap break-words font-serif text-[16px] leading-relaxed text-paper">{m.message}</p>
              <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-paper-faint">
                <span>Sent from</span>
                {m.piece_id !== null
                  ? <Link to={pieceHref(sectionPath, m.piece_id)} className={`${link} text-[12px]`}>{m.piece_title}</Link>
                  : m.page_url
                    ? <a href={m.page_url} target="_blank" rel="noreferrer" className={`${link} text-[12px] break-all`}>{m.page_label}</a>
                    : <span>{m.page_label}</span>}
              </div>
              {editor && <FeedbackThread m={m} publicationSlug={publicationSlug} onDirty={onDirty} />}
            </article>
          ))}
          {d?.truncated && <p className="m-0 text-[12px] text-paper-faint">Showing the newest {d.items.length} of {d.total}.</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-500 px-7 py-4">
          {showInbox ? <Link to="/feedback" className={`${link} text-[13px]`}>Open the Feedback inbox</Link> : <span />}
          <button type="button" onClick={onClose} className={btn}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
